import React, { useMemo, useState } from "react";
import { Button, Typography } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Feature } from "geojson";

import { RoadSideClassification } from "./modes/roadSelection";
import PreferenceSelector from "./PreferenceSelector";

import { Container, Footer, Section } from "./SelectedPaths.styles";

export interface SideValues {
  sideA: string | null;
  sideB: string | null;
  crossingSide: "sideA" | "sideB" | null;
}

interface RoadSelectedPathsProps {
  features: Feature[];
  roadSideData: RoadSideClassification;
  onCancel: () => void;
  onApply: (sideValues: SideValues) => void;
}

const sideLabel = (side: string): string => `${side.charAt(0).toUpperCase()}${side.slice(1)} side`;

const RoadSelectedPaths: React.FC<RoadSelectedPathsProps> = ({ features, roadSideData, onCancel, onApply }) => {
  const [sideAValue, setSideAValue] = useState<string | null>(null);
  const [sideBValue, setSideBValue] = useState<string | null>(null);
  const [crossingSide, setCrossingSide] = useState<"sideA" | "sideB" | null>(null);

  const { sideAFeatures, sideBFeatures, crossingCount } = useMemo(() => {
    const sideA: Feature[] = [];
    const sideB: Feature[] = [];
    let crossings = 0;

    for (const feature of features) {
      const id = String(feature.id ?? feature.properties?.id ?? "");
      if (roadSideData.crossingIds.has(id)) {
        crossings += 1;
      } else if (roadSideData.sideAIds.has(id)) {
        sideA.push(feature);
      } else if (roadSideData.sideBIds.has(id)) {
        sideB.push(feature);
      }
    }

    return { sideAFeatures: sideA, sideBFeatures: sideB, crossingCount: crossings };
  }, [features, roadSideData]);

  const adjustedCount = useMemo(() => {
    let count = 0;
    if (sideAValue !== null) count += sideAFeatures.length;
    if (sideBValue !== null) count += sideBFeatures.length;
    if (crossingSide !== null) count += crossingCount;
    return count;
  }, [sideAValue, sideBValue, crossingSide, sideAFeatures.length, sideBFeatures.length, crossingCount]);

  const canApply = sideAValue !== null || sideBValue !== null;

  const handleCrossingSideChange = (_: React.MouseEvent<HTMLElement>, newValue: "sideA" | "sideB" | null) => {
    setCrossingSide(newValue);
  };

  return (
    <Container>
      <PreferenceSelector label={sideLabel(roadSideData.sideA)} features={sideAFeatures} value={sideAValue} onChange={setSideAValue} />

      <PreferenceSelector label={sideLabel(roadSideData.sideB)} features={sideBFeatures} value={sideBValue} onChange={setSideBValue} />

      {crossingCount > 0 && (
        <Section>
          <Typography variant="body2" fontWeight={500}>
            {crossingCount} crossing{crossingCount !== 1 ? "s" : ""}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Apply preference from:
          </Typography>
          <ToggleButtonGroup
            value={crossingSide}
            exclusive
            size="small"
            onChange={handleCrossingSideChange}
            sx={{ width: "100%", "& .MuiToggleButton-root": { flex: 1, textTransform: "none" } }}
          >
            <ToggleButton value="sideA">{sideLabel(roadSideData.sideA)}</ToggleButton>
            <ToggleButton value="sideB">{sideLabel(roadSideData.sideB)}</ToggleButton>
          </ToggleButtonGroup>
        </Section>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {adjustedCount} feature{adjustedCount !== 1 ? "s" : ""} will be adjusted when you apply
      </Typography>

      <Footer>
        <Button color="primary" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ ml: 1, px: 2 }}
          disabled={!canApply}
          onClick={() => onApply({ sideA: sideAValue, sideB: sideBValue, crossingSide })}
        >
          Apply
        </Button>
      </Footer>
    </Container>
  );
};

export default RoadSelectedPaths;
